import ScrollModal from "@/components/common/scroll-modal";
import {
  useCreateEvent,
  useCreateFiscalYear,
  useUpdateEvent,
  type CreateEventBody,
  type IFiscalYear,
} from "@/queries/calendar/calendar.api";
import { cn } from "@/utils/theme";
import {
  Avatar,
  AvatarFallback,
  Button,
  Checkbox,
  GroupTextField,
  GroupTextFieldRow,
  Heading,
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from "@elaniintc/goes-design-system";
import { useState, type ChangeEvent } from "react";
import {
  mapDaysToNumbers,
  months,
  weekDays,
  type DayType,
  type EventType,
} from "../utils/utils";

interface ConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  handleSubmit: (daysSelected: DayType[]) => void;
  enableDay: DayType[];
  currentFiscalYear: IFiscalYear | undefined;
  eventWeekend: EventType | undefined;
}

const ConfigModal = ({
  isOpen,
  onClose,
  handleSubmit,
  enableDay,
  currentFiscalYear,
  eventWeekend,
}: ConfigModalProps) => {
  const { showToast } = useToast();

  const [daysSelected, setDaySelected] = useState<DayType[]>(enableDay);
  const [fiscalYear, setFiscalYear] = useState<{ day: number; month: number }>({
    month: currentFiscalYear
      ? Number(currentFiscalYear.start_date.split("-")[0])
      : 1,
    day: currentFiscalYear
      ? Number(currentFiscalYear.start_date.split("-")[1])
      : 1,
  });

  const getMaxDayForMonth = (month: number) => {
    if (month === 2) return 28;
    if ([4, 6, 9, 11].includes(month)) return 30;
    return 31;
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>, day: DayType) => {
    const value = e.target.checked;

    if (value) {
      setDaySelected((prev) => [...prev, day]);
    } else {
      setDaySelected(daysSelected.filter((d) => d !== day));
    }
  };

  const handleFiscalYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let val = value;

    if (name === "day") {
      const max = getMaxDayForMonth(Number(fiscalYear.month));
      if (Number(val) > max) val = max.toString();
      if (Number(val) < 1) val = "1";
    }

    if (name === "month") {
      const max = getMaxDayForMonth(Number(val));
      if (fiscalYear.day > max) {
        setFiscalYear((prev) => ({
          ...prev,
          [name]: Number(val),
          day: Number(max),
        }));
      }
    }

    setFiscalYear((prev) => ({ ...prev, [name]: val }));
  };

  const getEndFiscalYear = (
    day: number,
    month: number,
    year: number = new Date().getFullYear(),
  ): string => {
    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() - 1);
    const newMonth = String(date.getMonth() + 1).padStart(2, "0");
    const newDay = String(date.getDate()).padStart(2, "0");

    return `${newMonth}-${newDay}`;
  };

  const { mutate: createFiscalYear, isPending: creatingF } =
    useCreateFiscalYear();
  const { mutateAsync: createEvent, isPending: creating } = useCreateEvent();
  const { mutateAsync: updateEvent, isPending: updating } = useUpdateEvent();

  const isPending = creatingF || creating || updating;

  const handleCreateFiscalYear = () => {
    const fn = eventWeekend ? updateEvent : createEvent;
    try {
      createFiscalYear({
        id: currentFiscalYear?.id || undefined,
        name: currentFiscalYear?.name ?? "Año Fiscal",
        start_date: `${fiscalYear.month.toString().padStart(2, "0")}-${fiscalYear.day.toString().padStart(2, "0")}`,
        end_date: getEndFiscalYear(fiscalYear.day, fiscalYear.month),
      });

      fn({
        id: eventWeekend?.id ?? undefined,
        start_date: null,
        end_date: null,
        name: eventWeekend?.name ?? "weekend",
        description: eventWeekend?.name ?? "weekend",
        type: "WEEKEND",
        recurring: true,
        recurrence_type: "WEEKLY",
        active: true,
        recurrence_meta: {
          weekdays: mapDaysToNumbers(daysSelected),
        },
      } as CreateEventBody);

      showToast({
        title: "Año Fiscal",
        description: "El año fiscal fue actualizado correctamente.",
        options: {
          type: "success",
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="action" shape="square" type="icon">
                <Icon type="fas" name="save" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
    } catch (e) {
      const error = e as Error;
      showToast({
        title: "Error al editar registro",
        description: error.message,
        options: {
          type: "error",
          duration: 5000,
        },
      });
    } finally {
      onClose();
    }
  };

  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={onClose}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] flex flex-col"
      header={
        <div className="flex items-center justify-between px-8 py-3">
          <Heading variant="h5" className="mb-0!">
            Editar fin de Semana
          </Heading>
          <Button
            type="button"
            onClick={onClose}
            variant="quiet"
            aria-label="Cerrar"
            disabled={isPending}
            className="w-8 h-8 cursor-pointer"
            data-testid="close-config-modal"
          >
            <Icon type="far" name="times" className="text-xl text-gray-500" />
          </Button>
        </div>
      }
      footer={
        <div className="w-full flex justify-end py-3 px-8 gap-3">
          <Button
            variant="tertiary"
            size="sm"
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="cursor-pointer"
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            size="sm"
            type="submit"
            className="cursor-pointer disabled:bg-neutral-object-accent disabled:text-on-disabled disabled:border-none disabled:shadow-none"
            form="new-process-form"
            disabled={!daysSelected.length || isPending}
            onClick={handleCreateFiscalYear}
          >
            Guardar Cambios
            {isPending && (
              <Icon
                data-testid="loading-indicator"
                type="far"
                name="spinner-third"
                className={cn("text-current", "animate-spin")}
              />
            )}
          </Button>
        </div>
      }
    >
      <form
        id="new-process-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(daysSelected);
          return onClose();
        }}
        className="flex flex-col flex-1 min-h-0 px-4 sm:px-8"
      >
        <div className="flex flex-col gap-6 pb-6">
          <div>
            <label className="font-semibold text-body-loud leading-l text-sm">
              Días seleccionados <span className="text-danger">*</span>
            </label>
            <div className="flex justify-between items-center rounded-lg pt-2 px-6 pb-3 border border-neutral bg-neutral-object-accent-silent text-body-quiet">
              {weekDays.map((day) => (
                <div key={day} className="flex flex-col">
                  <span className="font-bold text-body text-xs">{day}</span>
                  <Checkbox
                    data-testid="checkbox"
                    checked={daysSelected.includes(day)}
                    onChange={(e) => onChange(e, day)}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="font-semibold text-body-loud leading-l text-sm">
              Inicio del año fiscal <span className="text-danger">*</span>
            </label>

            <GroupTextField label="">
              <GroupTextFieldRow className="w-full items-center">
                <Input
                  group="left"
                  name="day"
                  value={fiscalYear.day}
                  onChange={handleFiscalYearChange}
                  type="number"
                />
                <Select
                  name="month"
                  value={fiscalYear.month.toString()}
                  onValueChange={(value) =>
                    handleFiscalYearChange({
                      target: { value, name: "month" },
                    } as ChangeEvent<HTMLInputElement>)
                  }
                >
                  <SelectTrigger group="right" className="w-full">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent className="border-neutral">
                    {months.map((month, index) => (
                      <SelectItem value={(index + 1).toString()}>
                        {month}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </GroupTextFieldRow>
            </GroupTextField>
          </div>
        </div>
      </form>
    </ScrollModal>
  );
};

export default ConfigModal;
